import { StyleSheet, css } from 'aphrodite'
import { useNavigate } from 'react-router-dom'
import type { StakeholderType } from '../../types/user'

interface InvoiceTypeModalProps {
  isOpen: boolean
  onClose: () => void
  stakeholderTypes?: StakeholderType[]
}

type InvoiceRoute = 'driver' | 'broker' | 'subhauler' | 'contractor'

const InvoiceTypeModal: React.FC<InvoiceTypeModalProps> = ({
  isOpen,
  onClose,
  stakeholderTypes = [],
}) => {
  const navigate = useNavigate()

  if (!isOpen) return null

  const handleSelect = (type: InvoiceRoute) => {
    navigate(`/invoices/create/${type}`)
    onClose()
  }

  const hasSubHauler = stakeholderTypes.includes(
    'SUB_HAULER' as StakeholderType,
  )
  const hasBroker = stakeholderTypes.includes('BROKER' as StakeholderType)
  const hasContractor = stakeholderTypes.includes(
    'CONTRACTOR' as StakeholderType,
  )

  return (
    <div className={css(styles.modalOverlay)}>
      <div className={css(styles.modalContent)}>
        <h2 className={css(styles.title)}>Select Invoice Type</h2>
        <div className={css(styles.buttonContainer)}>
          {hasSubHauler && (
            <button
              className={css(styles.button)}
              onClick={() => handleSelect('driver')}
            >
              <i className="fa-solid fa-truck" style={{ marginRight: 8 }} />
              Sub Hauler Invoice
            </button>
          )}
          {hasBroker && (
            <button
              className={css(styles.button)}
              onClick={() => handleSelect('broker')}
            >
              <i className="fa-solid fa-handshake" style={{ marginRight: 8 }} />
              Broker Invoice
            </button>
          )}
          {hasSubHauler && (
            <button
              className={css(styles.button)}
              onClick={() => handleSelect('subhauler')}
            >
              <i
                className="fa-solid fa-truck-moving"
                style={{ marginRight: 8 }}
              />
              Sub Hauler to Sub Hauler Invoice
            </button>
          )}
          {hasContractor && (
            <button
              className={css(styles.button)}
              onClick={() => handleSelect('contractor')}
            >
              <i className="fa-solid fa-hard-hat" style={{ marginRight: 8 }} />
              Contractor Invoice
            </button>
          )}
        </div>
        <button className={css(styles.closeButton)} onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: 'white',
    padding: '24px',
    borderRadius: '8px',
    width: '400px',
    maxWidth: '90%',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  title: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#111827',
    marginBottom: '24px',
    textAlign: 'center',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '24px',
  },
  button: {
    backgroundColor: '#1C64F2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      backgroundColor: '#1955CC',
    },
  },
  closeButton: {
    backgroundColor: '#F3F4F6',
    color: '#374151',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    width: '100%',
    ':hover': {
      backgroundColor: '#E5E7EB',
    },
  },
})

export default InvoiceTypeModal
